import { basicMsg, htmlMsg, valMsg } from "./swal_msg";
import { axiosProtectedReq, protectedReq } from "../helpers/requestHelper";
import { formatMonth } from "../helpers/admin/adminHelper";

const readAdminResponse = async response => {
    const body = await response.json();
    if (!response.ok || !body.status) {
        const error = new Error(body.msg || 'request-err');
        error.status = response.status;
        error.body = body;
        throw error;
    }
    return body.resp;
};

export const getAdminOrderList = async filters =>
    readAdminResponse(await protectedReq('order/list', filters, 'POST'));

export const getAdminOrderOptions = async () =>
    readAdminResponse(await protectedReq('order/options'));

export const searchAdminOrders = async query =>
    readAdminResponse(await protectedReq(`order/search?q=${encodeURIComponent(query)}`));

export const getAdminOrderDetail = async id =>
    readAdminResponse(await protectedReq(`order/${encodeURIComponent(id)}`));

export const getFulfillmentQueue = async (status, page = 1, pageSize = 10, filters = {}) => {
    const params = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
    Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
    });
    return readAdminResponse(await protectedReq(`order/fulfillment-queue?${params.toString()}`));
};

export const updateAdminOrderShipping = async (id, data) =>
    readAdminResponse(await protectedReq(
        `order/${encodeURIComponent(id)}/shipping`, data, 'PATCH'
    ));

export const updateAdminOrdersShippingBulk = async ids =>
    readAdminResponse(await protectedReq(
        'order/shipping/bulk', { action: 'deliver', ids }, 'PATCH'
    ));

export const exportAdminOrders = async filters => {
    const response = await protectedReq('order/export', filters, 'POST');
    if (!response.ok) {
        let body = {};
        try { body = await response.json(); } catch (error) {}
        throw new Error(body.msg || 'export-err');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pedidos.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// pedidos perdidos - administrador
export const getLostOrders = async(
    setCurrentDate, setFilter, setOrdersCache
) => {
    try {
        const resp = await protectedReq('order/get-incompleted');
        const body = await resp.json();
        if (body.status) {
            const {
                droppedOrders, years, today, currentYear, currentMonth, statuses
            } = body.resp;
            
            setCurrentDate({ years, today, currentYear, currentMonth });

            // filtro utilizado (año y mes actual)
            setFilter(e => ({
                ...e,  year: currentYear,
                months: [currentMonth], statuses
            }));

            if (droppedOrders.length > 0) {
                // llave para pedidos de este año y mes
                // ej: 2026-03
                const key = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
                // guardar pedidos en caché
                setOrdersCache(e => ({
                    ...e,
                    [key]: droppedOrders
                }));
            }
        } else
            valMsg('error', 'Pedidos incompletos',
            'No se pudo obtener la lista');
    } catch (err) {
        valMsg('error', 'Pedidos incompletos',
        'Hubo un incidente al obtener la lista');
    };
};

// obtener más pedidos incompletos acorde con filtros
export const getOrdersByFilter = async(
    year, months, setOrdersCache, setBackdrop
) => {
    try {
        const resp = await protectedReq('order/filtered', {
            year, months
        }, 'POST');
        const body = await resp.json();
        if (!body.status) {
            const { msg } = body;
            if (msg === 'no-results') {
                basicMsg('No se encontraron coincidencias');
                const emptyGroups = months.reduce((groups, month) => {
                    groups[`${year}-${formatMonth(month)}`] = [];
                    return groups;
                }, {});
                setOrdersCache(cache => ({ ...cache, ...emptyGroups }));
            } else if (msg === 'miss')
                basicMsg('Falta información de filtro aplicado');
            else if (msg === 'server-err')
                valMsg('error', 'Pedidos filtrados',
                'Ocurrió un incidente. Intente de nuevo, por favor');
            else
                valMsg('error', 'Pedidos filtrados',
                'No se pudo obtener la lista');
            return;
        }

        const { orders } = body.resp;
        // agrupar pedidos extraídos mediante key
        const grouped = orders.reduce((acc, order) => {
            const { orderDate: { year, month } } = order;
            const key = `${year}-${formatMonth(month)}`;

            if (!acc[key]) acc[key] = [];
            acc[key].push(order);

            return acc;
        }, {});
        // guardar pedidos en caché
        setOrdersCache(e => ({
            ...e, ...grouped
        }));
        return { status: true };
    } catch (err) {
        valMsg('error', 'Pedidos incompletos',
        'Hubo un incidente al obtener la lista');
        return { status: false };
    } finally {
        setBackdrop(false);
    };
};

// subir archivo
export const uploadOrderFile = async(file, order_num) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('order_num', order_num);
        const resp = await axiosProtectedReq('order/file', formData, 'POST');
        if (!resp.status) {
            if (resp.msg === 'file-not-uploaded')
                alert('Archivo no subido');
            else if (resp.msg === 'folder-not-created')
                alert('Carpeta no creada');
            else if (resp.msg === 'miss')
                alert(`Falta información de pedido #${order_num}`);
            else
                alert('Ocurrió un incidente');
        }
        return resp;
    } catch (err) {
        alert(`Pedido ${order_num}: No se pudo subir archivo`);
        return { status: false };
    }
};

export const odooOrders = async() => {
    try {
        const resp = await protectedReq('order/odoo');
        const body = await resp.json();
        return body;
    } catch (err) {
        htmlMsg('error', 'Pedidos en Odoo',
        `<p>No se pudo recuperar pedidos de BD</p><h6>Error: ${err}</h6>`);
        return { status: false };
    };
};
